import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, X, ChevronDown } from 'lucide-react';
import Footer from '../components/Footer';
import '../styles/eyestrain-clean.css';

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="faq-item">
      <button
        className="faq-question"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>{question}</span>
        <ChevronDown className={`faq-icon ${isOpen ? 'rotate' : ''}`} />
      </button>
      {isOpen && (
        <div className="faq-answer">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

const SteamEyeMask = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 21,
    minutes: 9,
    seconds: 31
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        let { days, hours, minutes, seconds } = prevTime;

        if (seconds > 0) {
          seconds--;
        } else {
          seconds = 59;
          if (minutes > 0) {
            minutes--;
          } else {
            minutes = 59;
            if (hours > 0) {
              hours--;
            } else {
              hours = 23;
              if (days > 0) {
                days--;
              }
            }
          }
        }

        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (num: number) => String(num).padStart(2, '0');

  return (
    <div className="advertorial-page eyestrain-page">
      <Helmet>
        <title>
          Why This Steam Eye Mask Works Differently for Dry, Tired & Strained Eyes (The Ocular Reset™)
        </title>
        <meta
          name="description"
          content="Discover the Ocular Reset™ steam eye spa: gentle warming steam + soft pressure + darkness — a pocket-sized reset for dry, tired and strained eyes at home or on the move."
        />
        <meta
          name="keywords"
          content="steam eye mask, dry eyes, tired eyes, eye strain relief, ocular reset, warming steam mask, meibomian glands, travel-friendly eye mask"
        />
        <meta
          property="og:title"
          content="Why This Steam Eye Mask Works Differently for Dry, Tired & Strained Eyes (The Ocular Reset™)"
        />
        <meta
          property="og:description"
          content="A pocket-sized steam eye spa for dry, tired and strained eyes — consistent warmth, gentle steam and darkness with no microwave, no cords, no mess."
        />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Why This Steam Eye Mask Works Differently for Dry, Tired & Strained Eyes (The Ocular Reset™)"
        />
        <meta
          name="twitter:description"
          content="See why people are switching from microwave compresses and cooling gels to the Ocular Reset™ steam eye spa — at home or anywhere."
        />
        <link rel="canonical" href="https://kickinon.com/pages/switch-off-ritual" />
      </Helmet>

      {/* Main Content */}
      <div className="main-content">
        {/* Trending Badge */}
        <div className="trending-badge">
          ⭐ Trending: The Pocket-Sized Steam Eye Spa for Dry, Tired & Strained Eyes
        </div>

        {/* Hero Section */}
        <section className="hero-section">
          <h1 className="hero-title eyestrain-title">
            Why this steam eye mask works differently for dry, tired & strained eyes (The Ocular Reset™)
          </h1>

          <p className="hero-subtitle">
            If you searched “steam eye mask”, you already know heat helps. What most people don’t realise is <strong>how</strong> that heat is delivered — and what you’re actually putting on your eyes — makes all the difference.
            <br />
            <br />
            Here are the <strong>7 reasons</strong> the <strong>Ocular Reset™ Steam Eye Spa</strong> is replacing microwaved compresses, gel masks, and reusable eye wraps for people who want <strong>real relief without the guesswork</strong>.
          </p>

          <div className="author-info">
            <p className="author-name">By Jennifer K.</p>
            <p className="publish-date">Last Updated December 2025</p>
          </div>

          <div className="update-notice">
            <strong>UPDATE:</strong> Selling out faster than expected — Evening
            Ritual & Travel Bundles still available for a limited time.
          </div>

          {/* Countdown removed */}
        </section>

        {/* Wide Hero Image */}
        <section className="wide-image-section">
          <div className="wide-image-placeholder">
            <img
              src="/images/WO_Grid-1b.jpg"
              alt="Unwinding with Kickin'On steam eye mask"
              loading="lazy"
              decoding="async"
            />
          </div>
        </section>

        {/* Reason 1 */}
        <section className="feature-section">
          <div className="feature-image">
         
               <img
              src="/images/WO_lady_KickinOn-Steam-Eye-Mask---Dry-Eyes-and-Eye-Strain-Relief-10.jpg"
              alt="Waking up feeling more rested, from tired and strained dry eyes"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
        

            <h2 className="feature-title">
              1. It supports the oil glands that resets the eye
            </h2>
            <p className="feature-description">
              Your eyes rely on tiny oil glands along the eyelids (often called meibomian glands) to help reduce moisture evaporation.
            </p>
            <p className="feature-description">
              When that oil thickens — from screens, dry air, stress, travel, or fatigue — eyes can feel dry, gritty, strained, and “tired” even after rest.
            </p>
            <p className="feature-description">
              Gentle warmth helps create a more comfortable environment around the lids. The Ocular Reset™ delivers steady, even warmth across the eye area — not hot spots, not cooling gels — so many people feel relief that lasts beyond the session.
            </p>
          </div>
        </section>

        {/* Reason 2 */}
        <section className="feature-section reverse">
          <div className="feature-image">
            <img
              src="/images/reason6.jpg"
              alt="Spa-like eye relaxation at home or while travelling"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
                <h2 className="feature-title">
              2. Warmths, like a compress without the bateria
            </h2>
            <p className="feature-description">
              Most eye compresses rely on surface heat. Helpful… but incomplete.
            </p>
            <p className="feature-description">
              The Ocular Reset™ creates gentle, consistent steam that warms the eye area evenly and adds moisture to environments that are usually dry (screens, air-con, heaters, flights).
            </p>
            <p className="feature-description">
              This isn’t about blasting heat. It’s about creating the right conditions for tired eyes to finally relax — so closing your eyes feels good again.
            </p>
          </div>
        </section>

        {/* Reason 3 */}
        <section className="feature-section">
          <div className="feature-image">
            <img
              src="/images/reason4.jpg"
              alt="Hygienic single-use steam eye mask"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">
              3. No microwaves. No damp towels. No bacteria guessing game.
            </h2>
            <p className="feature-description">
              Microwavable compresses sound harmless… until you think about it: warm + damp fabric, reused repeatedly, stored in bathrooms or bags.
            </p>
            <p className="feature-description">
              The Ocular Reset™ is single-use, sealed, and activates on its own — so you get the same fresh steam experience every time.
            </p>
            <p className="feature-description">
              No reheating. No reuse. No wondering what’s grown on it since last week. Just clean, consistent comfort.
            </p>
          </div>
        </section>

        {/* Reason 4 */}
        <section className="feature-section reverse">
          <div className="feature-image">
            <img
              src="/images/reason7.jpg"
              alt="Simple relaxing ritual with steam eye mask"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">
              4. A proper eye spa treatment — that fits in your bag
            </h2>
            <p className="feature-description">
              This isn’t a bulky mask you need to prep or plan around.
            </p>
            <p className="feature-description">
              Open the packet, place it on, and your eyes get warm steam + darkness for 10–15 minutes. That’s it.
            </p>
            <p className="feature-description">
              Use it at night, between meetings, after long days, or on the move. No cords, no liquids, no setup — just a pocket-sized eye spa session you’ll actually use.
            </p>
          </div>
        </section>

        {/* Reason 5 */}
        <section className="feature-section">
          <div className="feature-image">
            <img
              src="/images/reason2.jpg"
              alt="Calm night routine with steam eye mask"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">
              5. Relief for dry, tired & strained eyes — whatever caused them
            </h2>
            <p className="feature-description">
              Screens contribute, sure — but so do air-conditioning, heating, long focus sessions, stress, early starts, and travel.
            </p>
            <p className="feature-description">
              The Ocular Reset™ isn’t tied to one cause — it’s designed for the end result: eyes that feel overworked and ready to be done.
            </p>
            <p className="feature-description">
              That’s why people use it before sleep, mid-afternoon, after travel, or whenever their eyes feel “over it.”
            </p>
          </div>
        </section>

        {/* Reason 6 */}
        <section className="feature-section reverse">
          <div className="feature-image">
              <img
              src="/images/reason1.jpg"
              alt="Relaxing with steam eye mask before sleep"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">
              6. Travel-friendly by design (not as an afterthought)
            </h2>
            <p className="feature-description">
              Plane cabins are dry. Hotel rooms are unpredictable. Your routine disappears the moment you zip the suitcase.
            </p>
            <p className="feature-description">
              The Ocular Reset™ is compact, cordless, and liquid-free — no plugs, no batteries, no “where do I microwave this?” problem.
            </p>
            <p className="feature-description">
              It turns flights, commutes, and hotel nights into a controlled, calming eye spa moment — which is why many people keep a pack in every bag.
            </p>
          </div>
        </section>

        {/* Reason 7 */}
        <section className="feature-section">
          <div className="feature-image">
            <img
              src="/images/reason5.jpg"
              alt="Steam eye mask for screen-tired, travel-heavy lifestyle"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">
              7. Consistency beats clever gadgets
            </h2>
            <p className="feature-description">
              The biggest reason people switch? It works the same way, every time.
            </p>
            <p className="feature-description">
              Predictable warmth. Consistent steam. No user error. No overheating. No underwhelming “that’s it?” moment.
            </p>
            <p className="feature-description">
              If you’ve tried other eye masks and felt disappointed, this is usually why.
            </p>
          </div>
              <a
            style={{ textAlign: 'center' }}
            href="https://kickinon.com/products/kickinon-steam-eye-mask"
            className="final-message-cta"
          >
            TRY KICKIN’ON STEAM EYE MASK RISK-FREE
          </a>
        </section>

        {/* Reviews Section (unchanged) */}
        <section className="reviews-section">
          <h2 className="reviews-title">What people are saying</h2>
          <p className="reviews-subtitle">
            Over 10,000+ five-star reviews from people who added Kickin’On to
            their nights & carry-ons
          </p>

          <div className="reviews-grid">
            <div className="review-card">
              <div className="review-header">
                <span className="review-author">Sarah M.</span>
                <span className="review-stars">★★★★★</span>
              </div>
              <div className="review-verified">✓ Verified Purchase</div>
              <p className="review-text">
                “I used to lie in bed scrolling because I couldn’t switch off.
                Now I put this on, feel my eyes relax, and I drift off so much
                easier. My mornings feel a lot less brutal.”
              </p>
            </div>

            <div className="review-card">
              <div className="review-header">
                <span className="review-author">Jessica L.</span>
                <span className="review-stars">★★★★★</span>
              </div>
              <div className="review-verified">✓ Verified Purchase</div>
              <p className="review-text">
                “It’s like a tiny spa moment for my face. The warmth, the darkness, the
                quiet… I finally feel my whole body unclench. I wake up feeling way more rested than I expect based on my sleep.”
              </p>
            </div>

            <div className="review-card">
              <div className="review-header">
                <span className="review-author">Amanda K.</span>
                <span className="review-stars">★★★★★</span>
              </div>
              <div className="review-verified">✓ Verified Purchase</div>
              <p className="review-text">
                “This is my ‘day is officially over’ moment. Ten to fifteen
                minutes with the mask on and my brain finally gets the memo to slow down.”
              </p>
            </div>

            <div className="review-card">
              <div className="review-header">
                <span className="review-author">Michelle R.</span>
                <span className="review-stars">★★★★★</span>
              </div>
              <div className="review-verified">✓ Verified Purchase</div>
              <p className="review-text">
                “A lifesaver for flights. I use one after takeoff and actually
                arrive feeling semi-human instead of wrecked. It lives in my
                carry-on now.”
              </p>
            </div>

            <div className="review-card">
              <div className="review-header">
                <span className="review-author">Lauren T.</span>
                <span className="review-stars">★★★★★</span>
              </div>
              <div className="review-verified">✓ Verified Purchase</div>
              <p className="review-text">
                “Finally a ritual I’ve stuck with. I use Kickin’On most nights
                and before big days. My evenings feel calmer and my mornings less chaotic.”
              </p>
            </div>

            <div className="review-card">
              <div className="review-header">
                <span className="review-author">Rachel W.</span>
                <span className="review-stars">★★★★★</span>
              </div>
              <div className="review-verified">✓ Verified Purchase</div>
              <p className="review-text">
                “I keep a box in my nightstand and another in my work bag. On
                huge days I’ll use one in the afternoon and one at night. It’s
                my reset button.”
              </p>
            </div>
          </div>
              <a
            style={{ textAlign: 'center' }}
            href="https://kickinon.com/products/kickinon-steam-eye-mask"
            className="final-message-cta"
          >
            TRY KICKIN’ON STEAM EYE MASK RISK-FREE
          </a>
        </section>

        {/* Timeline Section (UPDATED COPY) */}
        <section className="timeline-section">
          <h2 className="timeline-title">
            What Most People Notice After Starting the Ocular Reset™
          </h2>

          <p className="timeline-subtitle">
            The shift isn’t dramatic overnight — it’s subtle, cumulative, and surprisingly noticeable once your eyes finally get consistent care 👇
          </p>

          <div className="timeline-grid">
            {/* Week 1–2 */}
            <div className="timeline-item">
              <div className="timeline-icon">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <circle cx="20" cy="20" r="18" fill="#88E1C1" opacity="0.25" />
                  <circle cx="20" cy="20" r="12" fill="#88E1C1" />
                </svg>
              </div>
              <div className="timeline-content">
                <h3 className="timeline-period">
                  Week 1–2: Immediate comfort & relief
                </h3>
                <p className="timeline-description">
                  You start using the mask when your eyes feel dry, tired, or strained — especially at night. Many people notice calmer, less gritty-feeling eyes and a more comfortable “shut down” at the end of the day.
                </p>
              </div>
            </div>

            {/* Week 3–4 */}
            <div className="timeline-item">
              <div className="timeline-icon">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <circle cx="20" cy="20" r="18" fill="#88E1C1" opacity="0.25" />
                  <circle cx="20" cy="20" r="12" fill="#88E1C1" />
                </svg>
              </div>
              <div className="timeline-content">
                <h3 className="timeline-period">
                  Week 3–4: A predictable reset point
                </h3>
                <p className="timeline-description">
                  The Ocular Reset™ starts to feel familiar — the same warmth, the same steam, the same darkness. That consistency helps your eyes relax faster because they recognise the pattern.
                </p>
              </div>
            </div>

            {/* Month 2 */}
            <div className="timeline-item">
              <div className="timeline-icon">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <circle cx="20" cy="20" r="18" fill="#88E1C1" opacity="0.25" />
                  <circle cx="20" cy="20" r="12" fill="#88E1C1" />
                </svg>
              </div>
              <div className="timeline-content">
                <h3 className="timeline-period">
                  Month 2: Less eye fatigue carrying into the next day
                </h3>
                <p className="timeline-description">
                  Using your pocket-sized eye spa session becomes automatic. Many people say their eyes feel less “wrecked” by evening — especially after long screen days, dry offices, or travel.
                </p>
              </div>
            </div>

            {/* Month 3 */}
            <div className="timeline-item">
              <div className="timeline-icon">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <circle cx="20" cy="20" r="18" fill="#88E1C1" opacity="0.25" />
                  <circle cx="20" cy="20" r="12" fill="#88E1C1" />
                </svg>
              </div>
              <div className="timeline-content">
                <h3 className="timeline-period">
                  Month 3: A habit you protect (like coffee, but quieter)
                </h3>
                <p className="timeline-description">
                  It becomes your go-to reset: before bed, after long days, on flights, and whenever your eyes feel overstimulated. Not a chore — relief you trust.
                </p>
              </div>
            </div>
          </div>

          <div className="timeline-cta">
            <a
              href="https://kickinon.com/products/kickinon-steam-eye-mask"
              className="timeline-cta-button"
            >
              START YOUR OCULAR RESET™
            </a>
          </div>

          <p className="timeline-cta-note" style={{ textAlign: 'center', marginTop: "20px" }}>
            A simple eye spa treatment — done consistently — changes how tired eyes feel over time.
          </p>
        </section>

        {/* Comparison Section (subtitle tweaked slightly, structure kept) */}
        <section className="comparison-section">
          <h2 className="comparison-title">
            How Kickin’On Steam Eye Mask Stands Apart
          </h2>
          <p className="comparison-subtitle">
            Most “eye care” products only cool or coat. Kickin’On uses{' '}
            <strong>gentle, warming steam</strong> as part of the <strong>Ocular Reset™</strong> — steam + soft pressure + darkness — to create a pocket-sized eye spa moment you can use anywhere 👇
          </p>

          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th className="product-col">
                    <div className="product-icon-placeholder">
                      <img
                        src="/images/offer-section.jpg"
                        alt="Kickin’On Icon"
                        className="product-icon-img"
                      />
                    </div>
                  </th>
                  <th className="feature-col">Feature</th>
                  <th className="competitors-col">
                    Basic Cooling Masks & Gels
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td className="product-col">
                    <Check className="icon-check-green" />
                  </td>
                  <td className="feature-col">
                    Self-heating steam for a consistent eye spa session
                  </td>
                  <td className="competitors-col">
                    <X className="icon-x-red" />
                  </td>
                </tr>

                <tr>
                  <td className="product-col">
                    <Check className="icon-check-green" />
                  </td>
                  <td className="feature-col">
                    Ocular Reset™: steam + soft pressure + darkness
                  </td>
                  <td className="competitors-col">
                    <X className="icon-x-red" />
                  </td>
                </tr>

                <tr>
                  <td className="product-col">
                    <Check className="icon-check-green" />
                  </td>
                  <td className="feature-col">
                    Single-use, sealed, hygienic (no microwave re-use)
                  </td>
                  <td className="competitors-col">
                    <X className="icon-x-red" />
                  </td>
                </tr>

                <tr>
                  <td className="product-col">
                    <Check className="icon-check-green" />
                  </td>
                  <td className="feature-col">
                    Even warmth for 20+ minutes — no cords, no microwave
                  </td>
                  <td className="competitors-col">
                    <X className="icon-x-red" />
                  </td>
                </tr>

                <tr>
                  <td className="product-col">
                    <Check className="icon-check-green" />
                  </td>
                  <td className="feature-col">
                    Pocket-sized treatment you can use anywhere
                  </td>
                  <td className="competitors-col">
                    <X className="icon-x-red" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Final Message (refreshed to match Ocular Reset) */}
        <section className="final-message">
          <h2 className="final-message-title">
            Reclaim calmer nights & easier days with{' '}
            <span className="brand-highlight">Kickin’On</span>
          </h2>

          <div className="final-message-intro">
            <p>
              If your eyes feel dry, tired, or strained — and you’re over the cycle of “try a thing, forget the thing” — you’re not alone.
            </p>

            <p>
              <strong>Kickin’On Steam Eye Mask</strong> is built around one simple idea: give your eyes a real reset, and the rest of your day (and night) feels easier.
            </p>

            <p>
              The <strong>Ocular Reset™</strong> is a pocket-sized eye spa session: warm steam + soft pressure + darkness — a “power down” cue your body can actually understand.
            </p>

            <p>
              No complicated routine. No cords. No microwave towels. Just a small, consistent habit you can use at home, at work, on flights, and in the in-between moments when your eyes are begging for a break.
            </p>
          </div>

          <div className="photo-grid-container">
            <div className="photo-grid">
              <div className="photo-item">
                <div className="photo-placeholder">👁️</div>
              </div>
              <div className="photo-item">
                <div className="photo-placeholder">😌</div>
              </div>
              <div className="photo-item">
                <div className="photo-placeholder">✨</div>
              </div>
              <div className="photo-item">
                <div className="photo-placeholder">🌿</div>
              </div>

              <div className="photo-item photo-center">
                <div className="guarantee-badge">
                  <div className="badge-stars">★ ★ ★ ★ ★</div>
                  <div className="badge-seal">
                    <div className="badge-title">RISK-FREE</div>
                    <div className="badge-percentage">30-DAY</div>
                    <div className="badge-subtitle">TRY IT OUT</div>
                  </div>
                  <div className="badge-rating">rated 4.7/5</div>
                </div>
              </div>

              <div className="photo-item">
                <div className="photo-placeholder">💤</div>
              </div>
              <div className="photo-item">
                <div className="photo-placeholder">😊</div>
              </div>
              <div className="photo-item">
                <div className="photo-placeholder">💚</div>
              </div>
              <div className="photo-item">
                <div className="photo-placeholder">🌟</div>
              </div>
            </div>
          </div>

          <ul className="final-benefits-list">
            <li>
              <Check className="benefit-check" /> Gentle steam for a quick, cosy eye spa reset
            </li>
            <li>
              <Check className="benefit-check" /> Feels soft and comforting around the eyelids
            </li>
            <li>
              <Check className="benefit-check" /> Supports a calmer wind-down (and mid-day reset) routine
            </li>
            <li>
              <Check className="benefit-check" /> Fresh, single-use & hygienic
            </li>
            <li>
              <Check className="benefit-check" /> Cord-free, travel-friendly & ready anywhere
            </li>
          </ul>

          <p className="final-message-closing">
            Your calm matters. Your rest matters. And your eyes deserve a soft landing — after screens, dry air, long days, and long flights.
          </p>

          <a
            style={{ textAlign: 'center' }}
            href="https://kickinon.com/products/kickinon-steam-eye-mask"
            className="final-message-cta"
          >
            TRY KICKIN’ON STEAM EYE MASK RISK-FREE
          </a>

          <p className="final-message-note" style={{ textAlign: 'center', marginTop: "20px" }}>
            Still here. Still tired. Still Kickin’On.
          </p>
        </section>

        {/* Final CTA Section (UPDATED COPY, same images/links/classes) */}
        <section className="final-cta-section">
          <div className="cta-two-column">
            <div className="cta-image-column">
              <img
                src="/images/offer-section.jpg"
                alt="Kickin'On Steam Eye Mask - Buy 2 Get 1 Free"
                className="cta-offer-image"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="cta-content-column">
              <h3 className="final-cta-title">
                👉 Start Your Ocular Reset™ Eye Spa Treatment
              </h3>
              <p className="final-cta-subtitle">
                Kickin’On Steam Eye Mask — The Pocket-Sized Steam Eye Spa for Dry, Tired & Strained Eyes
              </p>
              <div className="final-cta-badge">
                Buy 2 Get 1 FREE — Limited Time Only
              </div>
              <a
                href="https://kickinon.com/products/kickinon-steam-eye-mask"
                target="_blank"
                rel="noopener noreferrer"
                className="final-cta-button"
              >
                GET YOUR STEAM MASK BUNDLE
              </a>
              <p className="final-cta-note">
                Tiny mask. Big reset for dry eyes, tired eyes & busy lives.
              </p>
              <p className="final-guarantee">30-Day Risk-Free Trial</p>
            </div>
          </div>
        </section>

        {/* FAQ Section (UPDATED COPY) */}
        <section className="faq-section">
          <h2 className="faq-title">Frequently Asked Questions</h2>

          <div className="faq-container">
            <FAQItem
              question="How long does each Ocular Reset™ Steam Eye Mask last?"
              answer="Each mask provides a consistent 20–25 minutes of gentle, warming steam — long enough to fully relax the eye area without overheating or fading too quickly."
            />

            <FAQItem
              question="Can I use it every day?"
              answer="Yes. The Ocular Reset™ is designed for regular use. Many people use it nightly, after long screen days, during travel, or whenever their eyes feel dry or strained — and because it’s single-use and sealed, daily use stays simple and hygienic."
            />

            <FAQItem
              question="What does it feel like when it’s on?"
              answer="Most people describe it as soft, lightly weighted, and warm (not hot). As the steam builds, your world goes dark and the eye area starts to relax — like a pocket-sized eye spa treatment."
            />

            <FAQItem
              question="How is this different from microwave eye masks or warm towels?"
              answer="Microwave options rely on reheated fabric that can heat unevenly, cool quickly, and get reused over and over. The Ocular Reset™ is self-heating, single-use, sealed and hygienic — no reheating, no guessing, no damp towel routine."
            />

            <FAQItem
              question="Is it travel friendly?"
              answer="Absolutely. Each mask is compact, cordless and liquid-free, making it perfect for carry-ons, handbags, work bags and hotel nights — no batteries, no plugs, no fuss."
            />

            <FAQItem
              question="Do I need to change anything else in my routine?"
              answer="No. You don’t need to overhaul your life. Just use the Ocular Reset™ as a small reset point when your eyes need it — and let the steam, warmth, and darkness do the work."
            />
          </div>
        </section>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SteamEyeMask;
